<template>
    <div class="homepage" :class="[`homepage--${currentLanguage}`]">
        <HomePageHeader :allow-sticky="true" />
        <div v-if="currentLanguage === 'en'">
            <Hero />
            <ProblemsSolved />
            <EngagePoints />
            <WordMap />
            <UseCases />
            <PlayForFree />
        </div>
        <div v-else>
            <Banner v-if="currentLanguage === 'et'" />
            <FirstBlock />
            <EquationsSolved />
            <TeachersLoveIt />
            <UseCases />
            <Testimonials />
            <Tutorial />
            <TopicsCovered />
            <IncreasesMotivation />
            <LastCta />
            <ProjectFunding v-if="currentLanguage === 'et'" />
        </div>
        <HomePageFooter />
    </div>
</template>

<script>
import HomePageHeader from './elements/HomePageHeader.vue';
import Banner from './elements/Banner.vue';
import EquationsSolved from './elements/EquationsSolved.vue';
import Tutorial from './elements/Tutorial.vue';
import LastCta from './elements/LastCta.vue';
import HomePageFooter from './elements/HomePageFooter.vue';
import Testimonials from './elements/Testimonials.vue';
import TeachersLoveIt from './elements/TeachersLoveIt.vue';
import TopicsCovered from './elements/TopicsCovered.vue';
import UseCases from './sections/UseCases.vue';
import FirstBlock from './elements/FirstBlock.vue';
import IncreasesMotivation from './elements/IncreasesMotivation.vue';
import ProjectFunding from './elements/ProjectFunding.vue';

import Hero from './sections/Hero.vue';
import ProblemsSolved from './sections/ProblemsSolved.vue';
import EngagePoints from './sections/EngagePoints.vue';
import WordMap from './sections/WordMap.vue';
import PlayForFree from './sections/PlayForFree.vue';

import { Hotjar } from '@/core/services/Hotjar';
import { mapGetters } from 'vuex';
import 'vue3-carousel/dist/carousel.css';

export default {
    components: {
        HomePageHeader,
        Banner,
        FirstBlock,
        EquationsSolved,
        Tutorial,
        Testimonials,
        LastCta,
        ProjectFunding,
        HomePageFooter,
        TeachersLoveIt,
        TopicsCovered,
        UseCases,
        IncreasesMotivation,
        Hero,
        ProblemsSolved,
        EngagePoints,
        WordMap,
        PlayForFree,
    },
    computed: {
        ...mapGetters({
            currentLanguage: 'getCurrentLanguage',
            user: 'user',
        }),
    },
    mounted() {
        if (this.currentLanguage === 'en' && !this.user) {
            Hotjar.triggerLandingpageEnglish();
        }
    },
    async created() {
        await this.$store.dispatch('getCountry');
    },
};
</script>

<style lang="scss">
@use 'homepage--header.scss';
.homepage {
    width: 100%;

    &--en {
        .homepage__use-cases {
            @include mq($w-max-sm, max) {
                padding-bottom: 1rem;
            }

            .learning__title {
                @include mq($w-max-sm, max) {
                    margin-top: 0;
                }
            }
        }
    }

    .btn {
        text-transform: initial;
    }

    .btn-yellow--homepage,
    .btn-primary--homepage {
        font-style: normal;
        font-weight: bold;
        font-size: 1.625rem;
        text-align: center;
        padding: 1.5rem;
        margin: 0.625rem;
        opacity: 1;
        width: fit-content;
        border: 2px solid #ed6f27;
    }

    .btn-primary--homepage {
        color: #ffffff;
        background-color: #ed6f27;

        &:hover,
        &:focus,
        &:active {
            background-color: #d06122;
        }
    }

    .btn-yellow--homepage {
        border-color: transparent;
        min-height: auto;
        color: #ed6f27;

        &:hover,
        &:focus,
        &:active {
            color: #ed6f27;
            background-color: #ffd74a;
            border: 2px solid #ed6f27;
        }
    }

    .btn--play {
        padding: 1.5rem 3rem;
    }

    .video-player {
        border: 10px solid #1a003b;
        border-radius: 10px;
        box-shadow: 0 4px 200px rgba(255, 255, 255, 0.2);

        .placeholder {
            height: 100%;
            width: 100%;
            overflow: hidden;
            min-height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            @include mq($w-min-tablet, min) {
                width: 500px;
                height: 400px;
            }

            @include mq($w-min-md, min) {
                width: 700px;
                height: 550px;
            }
            @include mq($w-min-desktop, min) {
                width: 900px;
            }

            &__play {
                opacity: 1;
                background: #ed6f27;
                width: 80px;
                height: 80px;
                padding-top: 14px;
                padding-left: 8px;
                line-height: 20px;
                border: 6px solid #a34c60;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                    background: #d66423;
                }

                .arrow {
                    margin: -10px 0 0 0;
                    border-left-color: #fff;
                    border-top-width: 20px;
                    border-bottom-width: 20px;
                    border-left-width: 30px;
                }
            }
        }
    }

    .skewed-divider {
        height: 20px;
        width: 100%;
        background-color: #572598;
        opacity: 0.5;
        transform: skewY(-2deg);

        &--reversed {
            transform: skewY(2deg);
        }

        &--yellow {
            background-color: #ffd74a;
        }

        &--white {
            background-color: #fff;
        }
    }

    .white-bg-wrapper {
        background-color: #fff;
    }

    .skewed-content {
        position: relative;
        z-index: 1;
        height: 110%;
        transform: skewY(-2deg);

        &--reversed {
            transform: skewY(2deg);
        }

        &__elements > * {
            transform: skewY(2deg);
        }

        &__elements {
            &--reversed {
                transform: skewY(-2deg);
            }
        }
    }

    .divider-wrapper {
        overflow: hidden;
    }

    .divider-tri-l {
        width: 0;
        height: 0;
        border-right: 0 solid transparent;
        border-bottom: 0 solid transparent;
        border-left: 2080px solid transparent;
        border-top: 150px solid #ae6be9;
        background-color: #9a46e4;
        overflow: hidden;
    }

    .divider-tri-r {
        width: 0;
        height: 0;
        border-right: 2080px solid transparent;
        border-bottom: 0 solid transparent;
        border-left: 0 solid transparent;
        border-top: 150px solid #9a46e4;
        overflow: hidden;
    }

    .white-bg-wrapper {
        background-color: #fff;
    }

    .carousel__prev,
    .carousel__next {
        background: transparent;
    }

    .carousel__pagination-button {
        width: 15px;
        height: 15px;
        border-radius: 50%;
        background: #c4c4c4;
    }

    .carousel__pagination-item {
        padding: 0 5px;
    }

    .carousel__pagination-button--active {
        background: #d1b5f8;
    }

    .carousel__pagination {
        z-index: 999;
        position: relative;
    }
}
</style>
